import React, { useEffect, useState } from "react";
import { Checkbox, DatePicker, Input, Switch } from "../../../../BP2Components";
import SimpleSelect from "../../../../BP2Components/Select/SimpleSelect";
import { createListForField, mapOperator } from "./TableFIlterScripts";
import { useIntl } from "react-intl";
import moment from "moment";
import { getDataForSelectFilters } from "../../../../../services/restAPI/ListsV2";

interface DraggableObjectContentProps {
   connectDragSource: any;
   object: any;
   columnList: any;
   setFilterFromList: any;
   listFilters: any;
   currentFilter: any;
   currentSection: any;
}

interface ISelectedColumns {
   label: string;
   meta: {
      filters: {
         operators: string[];
      };
      type: string;
      selectEntries: string | null;
   };
   options: {
      sumAllValues: boolean;
      sumPageValues: boolean;
      visible: boolean;
   };
   techName: string;
}

interface IOptionsFromSelectedEntries {
   value: string;
   label: string;
}

const DraggableObjectContent: React.FC<DraggableObjectContentProps> = ({
   connectDragSource,
   object,
   columnList,
   setFilterFromList,
   currentFilter,
   currentSection,
}) => {
   const [selectedColumn, setSelectedColumn] = useState<ISelectedColumns>();
   const [optionsFromSelectedEntries, setOptionsFromSelectedEntries] =
      useState<IOptionsFromSelectedEntries[]>();

   useEffect(() => {
      const newSelected = columnList?.find((column) => column?.techName === object?.techName);
      setSelectedColumn(newSelected);
   }, [object?.techName, columnList]);

   const findLabelByTechname = (techName, columnList) => {
      const label = columnList?.find((column) => column?.techName === techName)?.label;
      return label;
   };

   const intl = useIntl();

   const handleFieldChange = (
      userInput: any,
      field: string,
      sectionOrder: number,
      conditionOrder: number,
   ) => {
      if (field === "techName") {
         setSelectedColumn(columnList?.find((column) => column?.techName === userInput?.value));
      }

      setFilterFromList((prevFilter) => {
         if (prevFilter.id !== currentFilter.id) return prevFilter;
         return {
            ...prevFilter,
            filterSections: prevFilter.filterSections.map((section) => {
               if (section.order !== sectionOrder) return section;
               return {
                  ...section,
                  conditions: section.conditions.map((condition) => {
                     if (condition.order !== conditionOrder) return condition;
                     if (field === "techName") {
                        const newColumn = columnList?.find(
                           (column) => column?.techName === userInput?.value,
                        );
                        setSelectedColumn(newColumn);
                        let resetValue;
                        switch (newColumn?.meta?.type) {
                           case "LocalDate":
                           case "LocalDateTime":
                              resetValue = null;
                              break;
                           case "Boolean":
                              resetValue = [false];
                              break;
                           default:
                              resetValue = [""];
                        }

                        return {
                           ...condition,
                           techName: userInput.value,
                           value: resetValue,
                        };
                     }
                     return {
                        ...condition,
                        [field]:
                           field === "value"
                              ? Array.isArray(userInput)
                                 ? userInput
                                 : [userInput]
                              : (userInput?.value ?? userInput),
                     };
                  }),
               };
            }),
         };
      });
   };

   const handleDeleteCondition = (sectionOrder: number, conditionOrder: number) => {
      setFilterFromList((prevFilter) => {
         if (prevFilter.id !== currentFilter.id) return prevFilter;

         const updatedSections = prevFilter.filterSections
            .map((section) => {
               if (section.order !== sectionOrder) return section;

               const updatedConditions = section.conditions.filter(
                  (condition) => condition.order !== conditionOrder,
               );

               return {
                  ...section,
                  conditions: updatedConditions,
               };
            })
            .filter((section) => section.conditions.length > 0);

         return {
            ...prevFilter,
            filterSections: updatedSections,
         };
      });
   };

   const createOperatorsList = (avalibleOperators) => {
      const operatorsList = avalibleOperators?.map((operator) => ({
         value: operator,
         label: mapOperator(operator, intl),
      }));
      return operatorsList;
   };

   const getMomentValue = (value: any, format: string) => {
      if (
         value &&
         Array.isArray(value) &&
         value[0] &&
         typeof value[0] === "string" &&
         moment(value[0], format, true).isValid()
      ) {
         return moment(value[0], format);
      }
      return undefined;
   };

   useEffect(() => {
      if (selectedColumn?.meta?.selectEntries) {
         const filterData = getDataForSelectFilters(selectedColumn?.meta?.selectEntries).then(
            (res) => setOptionsFromSelectedEntries(res.data),
         );
      }
   }, [selectedColumn?.meta?.selectEntries]);

   const renderFieldInput = () => {
      //@ts-ignore
      switch (selectedColumn?.meta?.type) {
         case "Boolean":
            return (
               <div style={{ paddingLeft: "30px" }} className="field-select-container">
                  <Checkbox
                     checked={object?.value?.[0] ?? false}
                     key={selectedColumn?.techName}
                     onChange={(e) =>
                        handleFieldChange(
                           e.target.checked,
                           "value",
                           currentSection?.order,
                           object?.order,
                        )
                     }
                  />
               </div>
            );
         case "String":
            return (
               <Input
                  className="field-select-container"
                  key={selectedColumn?.techName}
                  onChange={(e) =>
                     handleFieldChange(
                        e.target?.value,
                        "value",
                        currentSection?.order,
                        object?.order,
                     )
                  }
                  value={object?.value?.[0] ?? ""}
                  disabled={!selectedColumn}
               />
            );
         case "Int":
         case "Long":
         case "Double":
         case "Float":
         case "BigDecimal":
            return (
               <Input
                  type="number"
                  className="field-select-container"
                  key={selectedColumn?.techName}
                  onChange={(e) =>
                     handleFieldChange(
                        e.target?.value,
                        "value",
                        currentSection?.order,
                        object?.order,
                     )
                  }
                  value={object?.value?.[0] ?? ""}
                  disabled={!selectedColumn}
               />
            );

         case "LocalDate":
            return (
               <DatePicker
                  className="field-select-container"
                  key={selectedColumn?.techName}
                  onChange={(e) =>
                     handleFieldChange(
                        moment(e).format("YYYY-MM-DD"),
                        "value",
                        currentSection?.order,
                        object?.order,
                     )
                  }
                  value={getMomentValue(object?.value, "yyyy-MM-DD")}
               />
            );
         case "LocalDateTime":
            return (
               <DatePicker
                  className="field-select-container"
                  key={selectedColumn?.techName}
                  size="large"
                  showTime
                  onChange={(e) =>
                     handleFieldChange(
                        moment(e).format("YYYY-MM-DD HH:mm:ss"),
                        "value",
                        currentSection?.order,
                        object?.order,
                     )
                  }
                  value={getMomentValue(object?.value, "YYYY-MM-DD HH:mm:ss")}
               />
            );

         default:
            return (
               <Input
                  className="field-select-container"
                  key={selectedColumn?.techName}
                  onChange={(e) =>
                     handleFieldChange(
                        e.target?.value,
                        "value",
                        currentSection?.order,
                        object?.order,
                     )
                  }
                  value={object?.value?.[0] ?? ""}
                  disabled={!selectedColumn}
               />
            );
      }
   };

   return (
      <div className="draggable-object-container">
         <Checkbox
            onChange={(e) =>
               handleFieldChange(e.target.checked, "active", currentSection?.order, object?.order)
            }
            checked={object?.active}
         />
         {connectDragSource(
            <div className="drag-button">
               <span className="material-symbols-outlined">drag_indicator</span>
            </div>,
         )}
         <div className="condition-switch-container">
            <Switch
               checked={
                  object?.joinOperator === "AND"
                     ? true
                     : object?.joinOperator === ""
                       ? "empty"
                       : false
               }
               size="default"
               onChange={(e) =>
                  handleFieldChange(
                     { value: e ? "AND" : "OR" },
                     "joinOperator",
                     currentSection?.order,
                     object?.order,
                  )
               }
               label="AND"
               label2="OR"
               customLabelStyle={{
                  color: "white",
                  marginLeft: "7px",
                  position: "relative",
               }}
               customLabel2Style={{
                  color: "white",
                  marginLeft: "25px",
               }}
               customBackgroundColor="#FFB400"
            />
         </div>
         <SimpleSelect
            onChange={(e) => handleFieldChange(e, "techName", currentSection?.order, object?.order)}
            customSelectContainerClassName="field-select-container"
            customSelectClassname="field-select"
            placeholder={
               object?.techName
                  ? findLabelByTechname(object?.techName, columnList)
                  : intl?.formatMessage({ id: "selectColumn" })
            }
            options={createListForField(columnList)}
         />
         <SimpleSelect
            customSelectContainerClassName="equation-select-container"
            customSelectClassname="equation-select"
            placeholder={mapOperator(object?.operator, intl)}
            onChange={(e) => handleFieldChange(e, "operator", currentSection?.order, object?.order)}
            options={createOperatorsList(selectedColumn?.meta?.filters?.operators) || []}
         />
         {!!selectedColumn?.meta?.selectEntries ? (
            <SimpleSelect
               customSelectContainerClassName="field-select-container"
               customSelectClassname="field-select"
               placeholder={object.value[0] ?? ""}
               onChange={(e) =>
                  handleFieldChange(e.label, "value", currentSection?.order, object?.order)
               }
               options={optionsFromSelectedEntries || []}
            />
         ) : (
            renderFieldInput()
         )}

         <span
            onClick={() => handleDeleteCondition(currentSection?.order, object?.order)}
            style={{ cursor: "pointer" }}
            className="material-symbols-outlined"
         >
            delete
         </span>
      </div>
   );
};

export default DraggableObjectContent;
